import { useTranslation } from 'react-i18next';
import type { SystemSettingResponse } from '../types/setting';

interface SettingResetConfirmProps {
  open: boolean;
  setting: SystemSettingResponse | null;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: (key: string) => void;
}

export function SettingResetConfirm({
  open,
  setting,
  isLoading,
  onClose,
  onConfirm,
}: SettingResetConfirmProps) {
  const { t } = useTranslation();
  if (!open || !setting) return null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!isLoading) onConfirm(setting.settingKey);
    }
  };

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="reset-confirm-title"
      aria-describedby="reset-confirm-desc"
      onKeyDown={handleKeyDown}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--color-overlay-backdrop)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
      }}
      onClick={onClose}
    >
      <div
        role="document"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          width: '100%',
          maxWidth: 480,
          boxShadow: 'var(--shadow-lg)',
          margin: 'var(--spacing-4)',
        }}
      >
        <h2
          id="reset-confirm-title"
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg)',
            color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-family-sans)',
          }}
        >
          {t('systemSettings.reset.confirm.title')}
        </h2>

        <p
          id="reset-confirm-desc"
          style={{
            marginTop: 'var(--spacing-4)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.6,
          }}
        >
          <code
            style={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              background: 'var(--color-surface-muted)',
              padding: '1px var(--spacing-1)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            {setting.settingKey}
          </code>
          {' '}{t('systemSettings.reset.confirm.description', { settingKey: setting.settingKey, currentValue: setting.value, defaultValue: setting.defaultValue })}
        </p>

        <div
          style={{
            marginTop: 'var(--spacing-6)',
            display: 'flex',
            gap: 'var(--spacing-2)',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('systemSettings.reset.confirm.button.cancel')}
          </button>
          <button
            type="button"
            aria-busy={isLoading}
            disabled={isLoading}
            onClick={() => !isLoading && onConfirm(setting.settingKey)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-warning)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: isLoading ? 0.7 : 1,
              minWidth: 100,
            }}
          >
            {isLoading ? t('systemSettings.edit.button.loading') : t('systemSettings.reset.confirm.button.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
